// modules/rental.js — Prenájom (požičovňa vecí): agreement + stavový automat + poplatok.
// Reálna SK vertikála (požičovňa náradia/vybavenia/e-bike). Vec = katalógová položka; prenájom =
// records riadok (module='rental'), lifecycle v `status`, refs/dáta/sadzba v custom. Skladá sa so
// skladom (item_ref = stock_item), NEpridáva tabuľku (vzor sklad.js). 0 fetch / 0 LLM / 0 DDL.
//
// Stavový automat (vzor poLifecycle/dealPipeline, deep-frozen, tx read-guard-write, injektovaný čas,
// audit história): reserved → picked_up → returned / cancelled. returned/cancelled TERMINÁLNE.
// „Po termíne" (overdue) NIE JE uložený stav — je ODVODENÝ z due_date < dnes (ako sklad = reduce
// pohybov), takže sa nikdy nerozsynchronizuje.
//
// DOUBLE-BOOKING: jedna vec = najviac JEDEN aktívny (reserved/picked_up) prenájom. createRental
// overuje dostupnosť VNÚTRI db.transaction (engine mutex serializuje) → na jednom zariadení
// dva súbežné prenájmy tej istej veci nevzniknú. (Cross-device súbežná rezervácia je soft konflikt
// riešený biznisom — rovnaká TOCTOU trieda ako invoice-level double, nedá sa dedupnúť det. id lebo
// prenájom NIE JE idempotentný — tá istá vec sa legitímne prenajíma opakovane.)
//
// ČAS: výhradne injektovaný deps.now() — žiadny ambientný čas v zdroji. Peniaze = int centy.
import { db, newCommonFields } from '../db';
import { isValidIsoDate, daysBetweenIso } from '../dateUtils';

// ─── MODULE_DEF descriptor ────────────────────────────────────────────────────
export const MODULE_DEF = {
  module: 'rental',
  label_sk: 'Prenájom',
  // accent = jediný zdroj brand farby modulu (menu+header+KPI).
  accent: 'green',
  entity: {
    base: 'record',
    custom_fields: [
      { stable_id: 'f_rate', label: 'Denná sadzba (€)', type: 'number' },
      { stable_id: 'f_due', label: 'Vrátiť do', type: 'date' },
      { stable_id: 'f_deposit', label: 'Kaucia (€)', type: 'number' },
    ],
  },
  view: { filter: { module: 'rental' }, sort: 'due_date_asc' },
  access: { collaborator: true, max_tier_out: 2 },
  metrics: [
    { slug: 'aktivne_prenajmy', reduce: 'count', filter: { module: 'rental', active: true } },
    { slug: 'po_termine', reduce: 'count', filter: { module: 'rental', overdue: true } },
  ],
};

export const RENTAL_MODULE = 'rental';
export const RENTAL_STATUSES = Object.freeze(['reserved', 'picked_up', 'returned', 'cancelled']);
export const TERMINAL_RENTAL_STATUSES = Object.freeze(['returned', 'cancelled']);
export const RENTAL_TRANSITIONS = Object.freeze({
  reserved: Object.freeze(['picked_up', 'cancelled']),
  picked_up: Object.freeze(['returned']), // vrátenie; zrušiť sa dá len pred vyzdvihnutím
  returned: Object.freeze([]),
  cancelled: Object.freeze([]),
});

// Aktívny prenájom = drží vec (rezervovaný alebo vyzdvihnutý).
const ACTIVE_RENTAL_STATUSES = Object.freeze(['reserved', 'picked_up']);

// ─── Chyby ────────────────────────────────────────────────────────────────────
export class RentalError extends Error {
  constructor(code, message) {
    super(message || code);
    this.name = 'RentalError';
    this.code = code;
  }
}
const fail = (code, message) => { throw new RentalError(code, message); };

// ─── Validátory ───────────────────────────────────────────────────────────────
const isCents = (v) => Number.isInteger(v) && v >= 0;
const isNonEmpty = (v) => typeof v === 'string' && v.trim() !== '';
function assertDeps(deps) {
  if (typeof deps?.now !== 'function') fail('E_INVALID_INPUT', 'deps.now je povinná funkcia (injektovaný čas)');
}

// Živý aktívny prenájom pre danú vec (cudzie/poškodené records sa ticho preskočia).
const isActiveRentalFor = (r, itemRef) =>
  r != null && r.module === RENTAL_MODULE && r.deleted_at == null &&
  ACTIVE_RENTAL_STATUSES.includes(r.status) && r.custom?.item_ref === itemRef;

async function getLiveRental(id, store) {
  const rec = await store.get(id);
  if (!rec || rec.module !== RENTAL_MODULE || rec.deleted_at != null) {
    fail('E_NOT_FOUND', 'Prenájom neexistuje alebo je zmazaný');
  }
  return rec;
}

// ─── createRental — nový 'reserved' prenájom (dostupnosť overená V TX) ─────────
// input = { item_ref (povinné), customer_ref (povinné), rate_cents_per_day (int centy ≥0),
//           start_date, due_date (ISO, due ≥ start), deposit_cents? (int centy ≥0) }
export async function createRental(input, deps) {
  assertDeps(deps);
  if (!isNonEmpty(input?.item_ref)) fail('E_INVALID_INPUT', 'item_ref je povinný');
  if (!isNonEmpty(input?.customer_ref)) fail('E_INVALID_INPUT', 'customer_ref je povinný');
  if (!isCents(input.rate_cents_per_day)) fail('E_INVALID_INPUT', 'rate_cents_per_day musí byť celé nezáporné číslo v centoch');
  if (input.deposit_cents != null && !isCents(input.deposit_cents)) fail('E_INVALID_INPUT', 'deposit_cents musí byť celé nezáporné číslo v centoch');
  if (!isValidIsoDate(input.start_date)) fail('E_INVALID_INPUT', 'start_date musí byť valídny ISO dátum (YYYY-MM-DD)');
  if (!isValidIsoDate(input.due_date)) fail('E_INVALID_INPUT', 'due_date musí byť valídny ISO dátum (YYYY-MM-DD)');
  if (daysBetweenIso(input.start_date, input.due_date) < 0) fail('E_INVALID_INPUT', 'due_date nemôže byť pred start_date');

  const itemRef = input.item_ref;
  return db.transaction(async (tx) => {
    // Availability VNÚTRI tx — mutex serializuje, druhý súbežný create vidí committed aktívny prenájom.
    const rentals = await tx.records.filter((r) => isActiveRentalFor(r, itemRef)).toArray();
    if (rentals.length > 0) fail('E_ITEM_UNAVAILABLE', 'Vec už má aktívny prenájom');

    const at = deps.now();
    const row = {
      ...newCommonFields(),
      module: RENTAL_MODULE,
      status: 'reserved',
      entry_date: at.slice(0, 10),
      created_at: at,
      updated_at: at,
      custom: {
        item_ref: itemRef,
        customer_ref: input.customer_ref,
        rate_cents_per_day: input.rate_cents_per_day,
        start_date: input.start_date,
        due_date: input.due_date,
        deposit_cents: input.deposit_cents ?? 0,
        returned_date: null,
        status_history: [{ from: null, to: 'reserved', at, kind: 'create' }],
      },
    };
    await tx.records.add(row);
    return row;
  });
}

// ─── Jadro prechodu — read-guard-write v tx ───────────────────────────────────
async function transitionRental(id, toStatus, meta, deps, mutate) {
  assertDeps(deps);
  return db.transaction(async (tx) => {
    const rec = await getLiveRental(id, tx.records);
    const from = rec.status;
    if (TERMINAL_RENTAL_STATUSES.includes(from)) fail('E_TERMINAL_STATUS', `Status '${from}' je terminálny`);
    if (!(RENTAL_TRANSITIONS[from] || []).includes(toStatus)) {
      fail('E_INVALID_TRANSITION', `Prechod '${from}'→'${toStatus}' nie je definovaná hrana`);
    }
    const at = deps.now();
    const entry = { from, to: toStatus, at, kind: 'transition' };
    if (isNonEmpty(meta?.note)) entry.note = meta.note;
    let custom = {
      ...(rec.custom || {}),
      status_history: [...(rec.custom?.status_history || []), entry],
    };
    if (typeof mutate === 'function') custom = mutate(custom, at);
    const rev = (rec.rev || 1) + 1;
    await tx.records.update(id, { status: toStatus, custom, updated_at: at, rev });
    return { ...rec, status: toStatus, custom, updated_at: at, rev };
  });
}

// ─── pickup / return / cancel ─────────────────────────────────────────────────
export async function pickupRental(id, meta = {}, deps) {
  return transitionRental(id, 'picked_up', meta, deps);
}

// Vrátenie stampne returned_date (dátum tiku) — vstup pre výpočet poplatku.
export async function returnRental(id, meta = {}, deps) {
  return transitionRental(id, 'returned', meta, deps, (custom, at) => ({ ...custom, returned_date: at.slice(0, 10) }));
}

export async function cancelRental(id, meta = {}, deps) {
  return transitionRental(id, 'cancelled', meta, deps);
}

// ─── Čisté funkcie ────────────────────────────────────────────────────────────

// Účtovateľné dni = max(1, dní medzi start a end). Deň vrátenia sa neúčtuje (span); vrátenie v deň
// začiatku = 1 deň (minimum). end pred start alebo neplatný dátum → null (nefabrikuj).
export function rentalDays(startIso, endIso) {
  const d = daysBetweenIso(startIso, endIso);
  if (d == null || d < 0) return null;
  return Math.max(1, d);
}

// Spoločné jadro: dni(start→end) × sadzba; neplatné dáta → null (honest-null, nefabrikuj).
function feeFromEndCents(agreement, end) {
  if (!end) return null;
  const days = rentalDays(agreement?.custom?.start_date, end);
  if (days == null) return null;
  const rate = agreement?.custom?.rate_cents_per_day;
  if (!isCents(rate)) return null;
  return days * rate;
}

// FINÁLNY poplatok — VÝHRADNE z audit-stampnutého returned_date (t.j. len po vrátení).
// Nevrátený/zrušený prenájom nemá finálnu cenu → null. Žiadny end parameter — explicitný
// koniec by prebil returned_date a fabrikoval účet (money diera, viď rental.realdb REGRESIA).
export function rentalFeeCents(agreement) {
  return feeFromEndCents(agreement, agreement?.custom?.returned_date);
}

// ODHAD poplatku k danému koncu — „koľko by stálo, keby vrátil v asOfIso" (running picked_up)
// alebo quote pre reserved obdobie (asOfIso = due_date). NIE JE finálny účet — na účtovanie
// slúži rentalFeeCents. asOfIso povinný a valídny, inak null.
export function rentalFeeEstimateCents(agreement, asOfIso) {
  if (!isValidIsoDate(asOfIso)) return null;
  return feeFromEndCents(agreement, asOfIso);
}

// Po termíne = vyzdvihnutý prenájom, ktorého due_date je PRED daným dňom (STRIKTNE).
export function isOverdue(agreement, todayIso) {
  if (agreement?.status !== 'picked_up') return false;
  const d = daysBetweenIso(agreement.custom?.due_date, todayIso);
  return d != null && d > 0;
}

// Má vec živý aktívny prenájom? (dostupnosť pre createRental / UI)
export function isItemActivelyRented(records, itemRef) {
  const recs = Array.isArray(records) ? records : [];
  return recs.some((r) => isActiveRentalFor(r, itemRef));
}

// Živé aktívne prenájmy (reserved/picked_up).
export function activeRentals(records) {
  const recs = Array.isArray(records) ? records : [];
  return recs.filter(
    (r) => r != null && r.module === RENTAL_MODULE && r.deleted_at == null && ACTIVE_RENTAL_STATUSES.includes(r.status)
  );
}

// Prenájmy po termíne k danému dňu.
export function overdueRentals(records, todayIso) {
  return activeRentals(records).filter((r) => isOverdue(r, todayIso));
}
